import type { ReactElement } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  FileObjectType,
  LabeledData,
  QueryLoader,
  Text,
  toast,
} from "@jsq/ds/core";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { withClientContext } from "~/apollo/client";
import { FundAdminServiceDocumentType } from "~/graphql/mainEnums";
import { useGetFundAdminServicesQuery, useUploadFundDocsMutation } from "~/graphql/mainHooks";
import { type S3DocumentInput } from "~/graphql/mainTypes";
import useUploadFilesToS3 from "~/hooks/uploadFilesToS3/useUploadFilesToS3";
import { getDocumentTypeByServiceType } from "~/pages/ServiceRequests/utils/utils";
import { formatFullDateAndTime } from "~/utils/datetime";
import { getErrorMessage } from "~/utils/errors";
import { UploadAdditionalDocsButton } from "./components/UploadAdditionalDocsButton";
import DataTable from "./DataTable";
import DocumentDetailsCard from "./DocumentsDetailsCard";
import ObjectDetailsCard from "./ObjectDetailsCard";
import { DetailCategory, TableItemRowDetails, DetailCategoryItem } from "./types";

type RequestParams = {
  id: string;
};

function isDetailCategoryItemATable(
  type: DetailCategoryItem["type"],
  value: string | TableItemRowDetails[],
): value is TableItemRowDetails[] {
  return type === "table";
}

export const ServiceRequests = (): ReactElement => {
  const { id } = useParams<RequestParams>();
  const history = useHistory();

  const fundAdminServicesResponse = useGetFundAdminServicesQuery({
    variables: {
      serviceIds: [id],
    },
    context: withClientContext("staff"),
  });

  const isActiveOrClosedRequest = Boolean(
    fundAdminServicesResponse?.data?.canopyFundAdminServices?.edges?.length,
  );

  const [uploadFundDocs] = useUploadFundDocsMutation();
  const { uploadFilesToS3 } = useUploadFilesToS3();

  const viewingRequestService =
    fundAdminServicesResponse.data?.canopyFundAdminServices?.edges?.[0]?.node;

  const detailCategories: DetailCategory[] = JSON.parse(viewingRequestService?.details ?? "[]");
  const hasAssociatedObjects = Boolean(viewingRequestService?.objects?.length);
  const additionalDocuments = viewingRequestService?.documents.filter(
    (document) => document.documentType === "additional",
  );
  const requiredDocuments = viewingRequestService?.documents.filter(
    (document) => document.documentType !== "additional",
  );
  const hasRequiredDocuments = Boolean(requiredDocuments?.length);
  const requiredDocumentsType: FundAdminServiceDocumentType = hasRequiredDocuments
    ? getDocumentTypeByServiceType(viewingRequestService?.requestType)
    : FundAdminServiceDocumentType.Additional;

  const isCompletedRequest =
    viewingRequestService?.status === "solved" || viewingRequestService?.status === "closed";
  /**
   * We intentionally separate out the "Request details" section because
   * sometimes it isn't submitted with the form values and we always want
   * a "Request details" card to display.
   */
  const detailCategoriesWithoutRequestDetails: DetailCategory[] = detailCategories.filter(
    (category) => category.label.toLowerCase() !== "request details",
  );

  const requestDetailsCategory = detailCategories.find(
    (category) => category.label.toLowerCase() === "request details",
  );

  const handleUploadDocs = async (
    files: FileObjectType[],
    documentsType: FundAdminServiceDocumentType,
  ): Promise<void> => {
    // No files to upload.
    if (files.length === 0) return;

    try {
      const uploadResponse = await uploadFilesToS3(files.map((x) => x.file));
      const s3ObjectKeys = uploadResponse.map((s3File): S3DocumentInput => {
        return {
          objectKey: s3File.objectKey,
          name: s3File.file.name,
          size: s3File.file.size,
          type: s3File.file.type,
        };
      });

      await uploadFundDocs({
        context: withClientContext("staff"),
        variables: {
          documentsType: documentsType,
          fundAdminServiceId: parseInt(viewingRequestService?.id ?? "1") ?? 1,
          s3ObjectKeys: s3ObjectKeys,
        },
      });

      await fundAdminServicesResponse.refetch();
    } catch (e) {
      toast.error(getErrorMessage(e));
      return;
    }

    toast.success("Upload successful");
  };

  return (
    <ColumnContainer>
      <Column size="md">
        <QueryLoader result={fundAdminServicesResponse}>
          {isActiveOrClosedRequest ? (
            <>
              <div>
                <Text variant="h1" data-testid="requested-service-request-type">
                  {fundAdminServicesResponse.loading
                    ? "--"
                    : viewingRequestService?.requestType}
                </Text>
                <Text variant="secondary-xl">Fund administration request</Text>
              </div>
              {hasAssociatedObjects && (
                <ObjectDetailsCard
                  requestType={viewingRequestService?.requestType ?? ""}
                  objects={viewingRequestService?.objects ?? []}
                />
              )}
              <Card title="Request details">
                {viewingRequestService?.createdAt && (
                  <LabeledData
                    label="Request created"
                    value={formatFullDateAndTime(viewingRequestService?.createdAt)}
                    variant="row"
                  />
                )}
                {(viewingRequestService?.modifiedAt || viewingRequestService?.createdAt) && (
                  <LabeledData
                    label="Last updated"
                    value={formatFullDateAndTime(
                      viewingRequestService?.modifiedAt ?? viewingRequestService?.createdAt,
                    )}
                    variant="row"
                  />
                )}
                {requestDetailsCategory?.items?.map((item) => (
                  <LabeledData
                    key={item.label}
                    label={item.label}
                    value={item.value as string}
                    variant={item.type === "multi-line-text" ? "standard" : "row"}
                  />
                ))}
              </Card>
              {detailCategoriesWithoutRequestDetails.map((categoryDetails: DetailCategory) => (
                <Card key={categoryDetails.label} title={categoryDetails.label}>
                  {categoryDetails.items.map((item, index) =>
                    isDetailCategoryItemATable(item.type, item.value) ? (
                      <DataTable key={`${item.label}${index}`} rows={item.value} />
                    ) : (
                      <LabeledData
                        key={item.label}
                        label={item.label}
                        value={item.value}
                        variant={item.type === "multi-line-text" ? "standard" : "row"}
                      />
                    ),
                  )}
                </Card>
              ))}
              {hasRequiredDocuments && (
                <DocumentDetailsCard
                  cardTitle="Required documents"
                  documentList={requiredDocuments}
                  actionComponent={
                    <UploadAdditionalDocsButton
                      isCompletedRequest={isCompletedRequest}
                      handleUploadDocs={handleUploadDocs}
                      documentsType={requiredDocumentsType}
                      buttonText={"Upload required"}
                    />
                  }
                />
              )}
              <DocumentDetailsCard
                cardTitle="Relevant documents"
                documentList={additionalDocuments}
                actionComponent={
                  <UploadAdditionalDocsButton
                    isCompletedRequest={isCompletedRequest}
                    handleUploadDocs={handleUploadDocs}
                    documentsType={FundAdminServiceDocumentType.Additional}
                  />
                }
              />
            </>
          ) : (
            <EmptyState
              illustration="searching"
              title="There's nothing here"
              description="This page doesn't exist, or you don't have permission to view it. Contact your team's admin to confirm your permissions, or reach out to support."
            >
              {" "}
              <Button onClick={() => history.push(`/`)}>Back to home</Button>
            </EmptyState>
          )}
        </QueryLoader>
      </Column>
    </ColumnContainer>
  );
};
